import type { GridColDef } from '@mui/x-data-grid-premium';
import { defined, notDefined } from '@vy/null';
import { shortenString } from '@vy/utils';
import { ContractStatusChip } from '../../../../../components/design/ContractStatusChip';
import { Price } from '../../../../../components/design/Price';
import { VyTableCell } from '../../../../../components/new-design/table/vy-table/VyTableCell';
import { compareDatesDescending, formatDate } from '../../../../../utils/date';
import { formatNumberWithPrecision } from '../../../../../utils/number';
import { formatDueDate } from '../../../../../utils/format-due-date';
import type { CustomerMasterRow } from './customer-master-row-types';

const CELL_CONFIG = {
  headerClassName: 'text-sm',
  flex: 1,
  minWidth: 150,
  headerAlign: 'left',
  align: 'left',
} as const;

/**
 * Built-in customer + contract columns for the (now flat) Customer Master grid.
 *
 * Each row is a (customer, contract) pair — customer fields repeat across all
 * of a customer's contract rows; customers with no contracts produce a row
 * whose contract fields are empty.
 */
export const CUSTOMER_MASTER_REPORT_BUILTIN_COLUMNS: GridColDef<CustomerMasterRow>[] = [
  // ---- Customer columns ----
  { ...CELL_CONFIG, field: 'customerName', headerName: 'Customer name', align: 'left',
    valueGetter: (_value, row) => row.customerName,
    renderCell: ({ row }) => <VyTableCell value={row.customerName} containerClassName="justify-start" /> },
  { ...CELL_CONFIG, field: 'customerStatus', headerName: 'Customer status', minWidth: 120,
    valueGetter: (_value, row) => row.customerStatus ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.customerStatus} /> },
  { ...CELL_CONFIG, field: 'customerId', headerName: 'Customer ID',
    valueGetter: (_value, row) => row.customerId ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.customerId ?? '--'} /> },
  { ...CELL_CONFIG, field: 'legalName', headerName: 'Legal name', minWidth: 175,
    valueGetter: (_value, row) => row.legalName ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.legalName} /> },
  { ...CELL_CONFIG, field: 'customerAlias', headerName: 'Customer alias',
    valueGetter: (_value, row) => row.customerAlias ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.customerAlias} /> },
  { ...CELL_CONFIG, field: 'dueDate', headerName: 'Due date',
    valueGetter: (_value, row) => row.dueDate ?? null,
    renderCell: ({ row }) => <VyTableCell value={formatDueDate(row.dueDate)} /> },
  { ...CELL_CONFIG, field: 'parentCustomer', headerName: 'Parent customer',
    valueGetter: (_value, row) => row.parentCustomer ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.parentCustomer} /> },
  { ...CELL_CONFIG, field: 'crmId', headerName: 'CRM ID',
    valueGetter: (_value, row) => row.crmId ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.crmId} /> },
  { ...CELL_CONFIG, field: 'nsErpId', headerName: 'NS Customer ERP ID',
    valueGetter: (_value, row) => row.nsErpId ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.nsErpId} /> },
  { ...CELL_CONFIG, field: 'qbErpId', headerName: 'QB Customer ERP ID',
    valueGetter: (_value, row) => row.qbErpId ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.qbErpId} /> },
  { ...CELL_CONFIG, field: 'xeroErpId', headerName: 'Xero Customer ERP ID',
    valueGetter: (_value, row) => row.xeroErpId ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.xeroErpId} /> },
  { ...CELL_CONFIG, field: 'stripeId', headerName: 'Stripe ID',
    valueGetter: (_value, row) => row.stripeId ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.stripeId} /> },
  { ...CELL_CONFIG, field: 'subsidiary', headerName: 'Subsidiary',
    valueGetter: (_value, row) => row.subsidiary ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.subsidiary} /> },
  { ...CELL_CONFIG, field: 'wallet', headerName: 'Wallet',
    valueGetter: (_value, row) => row.wallet ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.wallet} /> },
  { ...CELL_CONFIG, field: 'createdAt', headerName: 'Created date',
    valueGetter: (_value, row) => row.createdAt,
    renderCell: ({ row }) => formatDate(row.createdAt) },
  { ...CELL_CONFIG, field: 'country', headerName: 'Country',
    valueGetter: (_value, row) => row.address?.country ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.address?.country} /> },
  { ...CELL_CONFIG, field: 'state', headerName: 'State',
    valueGetter: (_value, row) => row.address?.state ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.address?.state} /> },
  { ...CELL_CONFIG, field: 'city', headerName: 'City',
    valueGetter: (_value, row) => row.address?.city ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.address?.city} /> },
  { ...CELL_CONFIG, field: 'addressText', headerName: 'Address',
    valueGetter: (_value, row) => row.address?.addressText ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.address?.addressText} /> },
  { ...CELL_CONFIG, field: 'zipCode', headerName: 'Zip code',
    valueGetter: (_value, row) => row.address?.postalCode ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.address?.postalCode} /> },
  { ...CELL_CONFIG, field: 'customerType', headerName: 'Customer type',
    valueGetter: (_value, row) => row.customerType ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.customerType} /> },
  { ...CELL_CONFIG, field: 'taxId', headerName: 'Tax ID',
    valueGetter: (_value, row) => row.taxId ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.taxId} /> },
  { ...CELL_CONFIG, field: 'customerCurrency', headerName: 'Currency',
    valueGetter: (_value, row) => row.customerCurrency ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.customerCurrency} /> },
  { ...CELL_CONFIG, field: 'taxMethod', headerName: 'Tax method',
    valueGetter: (_value, row) => row.taxMethod ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.taxMethod} /> },
  { ...CELL_CONFIG, field: 'taxRate', headerName: 'Tax %',
    valueGetter: (_value, row) => row.taxRate ?? null,
    renderCell: ({ row }) => <VyTableCell value={defined(row.taxRate) ? `${row.taxRate}%` : null} /> },
  { ...CELL_CONFIG, field: 'manualReview', headerName: 'Manual review',
    valueGetter: (_value, row) => row.manualReview ?? null,
    renderCell: ({ row }) => <VyTableCell value={renderBooleanLabel(row.manualReview)} /> },
  { ...CELL_CONFIG, field: 'paymentProvider', headerName: 'Primary payment provider',
    valueGetter: (_value, row) => row.paymentProvider ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.paymentProvider} /> },
  { ...CELL_CONFIG, field: 'autoPay', headerName: 'Auto pay',
    valueGetter: (_value, row) => row.autoPay ?? null,
    renderCell: ({ row }) => <VyTableCell value={renderBooleanLabel(row.autoPay)} /> },
  { ...CELL_CONFIG, field: 'customerNotes', headerName: 'Customer notes',
    valueGetter: (_value, row) => row.customerNotes ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.customerNotes} /> },

  // ---- Contract columns ----
  { ...CELL_CONFIG, field: 'contractName', headerName: 'Contract name', align: 'left',
    valueGetter: (_value, row) => row.contractName ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.contractName} containerClassName="justify-start" /> },
  { ...CELL_CONFIG, field: 'contractId', headerName: 'Contract ID',
    valueGetter: (_value, row) => row.contractId ?? null,
    renderCell: ({ row }) => <VyTableCell value={defined(row.contractId) ? shortenString(row.contractId, 5) : '--'} containerClassName="justify-start" /> },
  { ...CELL_CONFIG, field: 'contractStatus', headerName: 'Contract status', minWidth: 250,
    valueGetter: (_value, row) => row.contractStatus ?? null,
    renderCell: ({ row }) => defined(row.contractStatus) ? <ContractStatusChip status={row.contractStatus} endDate={row.endDate} /> : null },
  { ...CELL_CONFIG, field: 'startDate', headerName: 'Start date',
    valueGetter: (_value, row) => row.startDate ?? null,
    renderCell: ({ row }) => defined(row.startDate) ? formatDate(row.startDate) : null,
    sortComparator: (leftDate: Date | null, rightDate: Date | null) => {
      if (leftDate === null && rightDate === null) return 0;
      if (leftDate === null) return 1;
      if (rightDate === null) return -1;
      return compareDatesDescending(leftDate, rightDate);
    } },
  { ...CELL_CONFIG, field: 'endDate', headerName: 'End date',
    valueGetter: (_value, row) => row.endDate ?? null,
    renderCell: ({ row }) => <VyTableCell value={defined(row.endDate) ? formatDate(row.endDate) : '--'} /> },
  { ...CELL_CONFIG, field: 'daysToEnd', headerName: 'Time to end',
    valueGetter: (_value, row) => row.daysToEnd ?? null,
    renderCell: ({ row }) => <VyTableCell value={getDaysToEndLabel(row.daysToEnd)} /> },
  { ...CELL_CONFIG, field: 'estimatedAmount', headerName: 'Contract Estimation',
    valueGetter: (_value, row) => row.estimatedAmount ?? null,
    valueFormatter: value => defined(value) ? formatNumberWithPrecision(value as number, 2) : '',
    renderCell: ({ row }) => defined(row.estimatedAmount) ? <Price>{row.estimatedAmount}</Price> : null },
  { ...CELL_CONFIG, field: 'mrr', headerName: 'MRR (USD)',
    valueGetter: (_value, row) => row.mrr ?? null,
    valueFormatter: value => defined(value) ? formatNumberWithPrecision(value as number, 2) : '',
    renderCell: ({ row }) => defined(row.mrr) ? <Price>{row.mrr}</Price> : null },
  { ...CELL_CONFIG, field: 'syncStatusUpdatedAt', headerName: 'SO Sync',
    valueGetter: (_value, row) => row.syncStatusUpdatedAt ?? null,
    renderCell: ({ row }) => <VyTableCell value={defined(row.syncStatusUpdatedAt) ? String(row.syncStatusUpdatedAt) : '--'} /> },
  { ...CELL_CONFIG, field: 'trialPeriod', headerName: 'Trial period', minWidth: 125,
    valueGetter: (_value, row) => row.trialPeriod ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.trialPeriod ?? '--'} /> },
  { ...CELL_CONFIG, field: 'autoRenewal', headerName: 'Auto renewal',
    valueGetter: (_value, row) => row.autoRenewal ?? null,
    renderCell: ({ row }) => <VyTableCell value={defined(row.autoRenewal) ? (row.autoRenewal ? 'Yes' : 'No') : null} /> },
  { ...CELL_CONFIG, field: 'durationMonths', headerName: 'Duration (months)',
    valueGetter: (_value, row) => row.durationMonths ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.durationMonths} /> },
  { ...CELL_CONFIG, field: 'purchaseOrder', headerName: 'Purchase order',
    valueGetter: (_value, row) => row.purchaseOrder ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.purchaseOrder} /> },
  { ...CELL_CONFIG, field: 'accountManager', headerName: 'AE',
    valueGetter: (_value, row) => row.accountManager ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.accountManager} /> },
  { ...CELL_CONFIG, field: 'salesforceOpportunityId', headerName: 'Salesforce Opportunity ID',
    valueGetter: (_value, row) => row.salesforceOpportunityId ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.salesforceOpportunityId} /> },
  { ...CELL_CONFIG, field: 'netSuiteSalesOrderId', headerName: 'NetSuite Sales Order ID',
    valueGetter: (_value, row) => row.netSuiteSalesOrderId ?? null,
    renderCell: ({ row }) => <VyTableCell value={row.netSuiteSalesOrderId} /> },
];

export const CONTACT_FIELDS = ['name', 'title', 'phone', 'email'] as const;

export function buildContactColumns(contactIndex: number): GridColDef<CustomerMasterRow>[] {
  const contactNumber = contactIndex + 1;

  return CONTACT_FIELDS.map((field) => {
    const capitalizedField = capitalizeFirst(field);
    return {
      ...CELL_CONFIG,
      field: `contact${contactNumber}${capitalizedField}`,
      headerName: `Contact ${contactNumber} - ${capitalizedField}`,
      valueGetter: (_value, row) => row.contacts?.[contactIndex]?.[field] ?? null,
      renderCell: ({ row }) => <VyTableCell value={row.contacts?.[contactIndex]?.[field]} />,
    };
  });
}

function capitalizeFirst(value: string): string {
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}

function renderBooleanLabel(value: boolean | undefined | null): string | null {
  if (notDefined(value)) return null;
  return value ? 'Yes' : 'No';
}

function getDaysToEndLabel(daysToEnd: number | undefined | null): string {
  if (notDefined(daysToEnd)) return 'Unlimited';
  if (daysToEnd < 0) return 'Expired';
  return `${daysToEnd} days`;
}
